<script setup>
import { ref, watch, onMounted, onBeforeMount, inject, reactive, computed } from 'vue';
import { useLevellingData } from '@/composables/useLevellingData';
import { useLevellingColumns } from '@/composables/useLevellingColumns';

import utils from '@/common/utils.js';
import PageDescription from '@/components/cPageDescription';
import HierarchySearch from '@/components/cHierarchySearch';
import DatePickerTextBox from '@/components/common/DatePickerTextBox';
import ReportContainer from '@/components/jobArchitecture/ReportContainer.vue';
import Tasks from '@/components/cTasks.vue';
import LevellingFramework from '@/components/levelling/framework.vue';
import LevellingReports from '@/components/levelling/LevellingReports.vue';

const props = defineProps({
  evaluation: { type: Boolean, default: false },
});

const emit = defineEmits(['openDocument', 'setLevel', 'editLevel', 'export']);

const store = inject('store');
const loginState = inject('loginState');
const passedParams = inject('passedParams', null);

// One-shot read of any deep-link preset (e.g. a homepage Stats tile linking here
// with statuses/doc-types preselected). `.get()` returns and clears the params,
// so read once at setup and hand the snapshot to the data composable.
const presetFilters =
  passedParams && typeof passedParams.get === 'function' ? passedParams.get() : null;

// --- Composables ---
const data = useLevellingData({ store, loginState, presetFilters });
const columns = useLevellingColumns({ data });

// --- Primary Tabs ---
const levellingTabs = reactive({
  primaryTabs: [
    { key: "home", title: "", icon: "mdi-home", iconColour: "#50B3CB", borderColour: "#A7D9E5", width: "120px", visible: true, default: true },
    { key: "job-architecture", title: "Job Architecture", icon: "mdi-file-tree", iconColour: "#FFCB00", borderColour: "#FFE580", width: "", visible: true },
    { key: "pay-equity", title: "Pay Equity", icon: "mdi-dots-triangle", iconColour: "#EA3785", borderColour: "#F7AFCE", width: "", visible: true },
    { key: "my-tasks", title: "My Tasks", icon: "mdi-clipboard-text-outline", iconColour: "#FFCB00", borderColour: "#FFE580", width: "", visible: true },
    { key: "levelling-framework", title: "Levelling Framework", icon: "mdi-view-grid", iconColour: "#CEDC26", borderColour: "#E2EA7D", width: "", visible: true },
  ],
  activeTab: null,
});

onBeforeMount(() => {
  const defaultTab = levellingTabs.primaryTabs.find(tab => tab.default);
  if (defaultTab) {
    levellingTabs.activeTab = defaultTab;
  }
});

const selectTab = (tab) => {
  levellingTabs.activeTab = tab;
};

// --- Component-local UI state ---
const isSidebarCollapsed = ref(false);
const sidebarTab = ref('filters');
const filtersExpanded = ref(true);

const totalRoles = computed(() => data.totalRolesCount.value);
const rolesLevelled = computed(() => data.rolesLevelledCount.value);
const rolesInProgress = computed(() => data.rolesInProgressCount.value);
const rolesToBeLevelled = computed(() => data.rolesToBeLevelledCount.value);

function evalStatusFor(d) {
  return data.getEvaluationStatus(d.doc_id).evaluation_status ?? d.evaluation_status;
}

// --- Handlers ---
function sanitize(val) {
  return utils.sanitize(val);
}

function handleClearSearch() {
  data.filterText.value = '';
  data.doFilter();
}

function handleRowsPerPage(opt) {
  data.rowsPerPage.value = opt;
  data.currentPage.value = 1;
  data.doFilter();
}

// --- Watchers ---
watch(data.isLoading, (val) => {
  if (!val) {
    if (columns.defaultColumnValues.value.length > columns.dtColumnsEnabled.value.length) {
      columns.initColumns();
    }
    data.fetchData();
  }
});

watch(data.sortColumn, () => {
  if (data.useIndex.value) data.doFilter();
});
watch(data.sortDesc, () => {
  if (data.useIndex.value) data.doFilter();
});

// --- Lifecycle ---
onMounted(() => {
  columns.initColumns();
  if (!data.isLoading.value) data.fetchData();
});
</script>

<template>
  <v-card class="levellingManager" flat>
    <!-- Primary Tab Lozenges -->
    <v-row class="flex-nowrap" style="padding:40px 40px 0px 40px">
      <v-col v-for="(t, ti) in levellingTabs.primaryTabs.filter(x => x.visible)"
          :style="{ width: t.width, maxWidth: t.width }" @click="selectTab(t)"
          :key="'tab' + ti">
          <div class="rm-primary-tab rm-box" style="cursor: pointer;"
            :style="t === levellingTabs.activeTab ? 'border: 4px solid ' + t.borderColour : ''"
            :class="t === levellingTabs.activeTab ? 'tab-selected' : ''">
          <v-icon v-if="t.icon" :color="t.iconColour" class="mr-4 rm-primary-tab-icon">{{ t.icon }}</v-icon>
          {{t.title}}
        </div>
      </v-col>
    </v-row>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Home Tab (current levelling view) -->
      <div v-if="levellingTabs.activeTab?.key === 'home'" class="tabItem levelling">
        <div class="levelling-container">
    <!-- Left Sidebar -->
    <div :class="['sidebar-wrapper', { collapsed: isSidebarCollapsed }]">
      <div :class="['sidebar', { collapsed: isSidebarCollapsed }]">
        <!-- Tabs -->
      <div v-if="!isSidebarCollapsed" class="sidebar-tabs">
        <button
          :class="['sidebar-tab', { active: sidebarTab === 'filters' }]"
          @click="sidebarTab = 'filters'"
        >
          <v-icon small>mdi-magnify</v-icon>
          Search &amp; Filters
        </button>
        <button
          :class="['sidebar-tab', { active: sidebarTab === 'explore' }]"
          @click="sidebarTab = 'explore'"
        >
          <v-icon small>mdi-file-tree-outline</v-icon>
          Explore
        </button>
      </div>

      <!-- Search & Filters Tab -->
      <div v-if="!isSidebarCollapsed && sidebarTab === 'filters'" class="sidebar-content">
        <div class="search-field-wrapper">
          <v-icon class="search-icon">mdi-magnify</v-icon>
          <input
            v-model="data.filterText.value"
            type="text"
            class="search-input"
            placeholder="Job Profile, Description, Family, etc..."
            @keypress.enter="data.doFilter"
          />
          <v-icon
            v-if="data.filterText.value"
            class="search-clear"
            small
            @click="handleClearSearch"
          >mdi-close</v-icon>
        </div>

        <div class="filters-container">
          <div class="filters-header" @click="filtersExpanded = !filtersExpanded">
            <v-icon small class="mr-1">{{ filtersExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <span class="filters-label">Filters</span>
            <span v-if="data.activeFilterCount.value > 0" class="filter-count">{{ data.activeFilterCount.value }}</span>
          </div>

          <template v-if="filtersExpanded">
          <div v-for="hc in data.hierarchyLevelColumns.value" :key="'hf' + hc.field" class="filter-group">
            <label class="filter-label">{{ hc.label }}</label>
            <v-select
              :value="data.hierarchyFieldFilters.value[hc.field]"
              :items="data.getDistinctValues(hc.field)"
              dense
              solo
              flat
              hide-details
              clearable
              placeholder="All"
              append-icon="mdi-chevron-down"
              :menu-props="{ closeOnContentClick: true, maxHeight: '400', 'offset-y': true }"
              class="filter-select"
              @change="data.setFieldFilter('hierarchyFieldFilters', hc.field, $event)"
            ></v-select>
          </div>

          <div v-for="ct in data.classifierTypes.value" :key="'cf' + ct.ct_id" class="filter-group">
            <label class="filter-label">{{ ct.ct_name }}</label>
            <v-select
              :value="data.classifierFieldFilters.value['classifier_' + ct.ct_id]"
              :items="data.getDistinctValues('classifier_' + ct.ct_id)"
              dense
              solo
              flat
              hide-details
              clearable
              placeholder="All"
              append-icon="mdi-chevron-down"
              :menu-props="{ closeOnContentClick: true, maxHeight: '400', 'offset-y': true }"
              class="filter-select"
              @change="data.setFieldFilter('classifierFieldFilters', 'classifier_' + ct.ct_id, $event)"
            ></v-select>
          </div>

          <!-- Doc Type filter -->
          <div class="filter-group">
            <label class="filter-label">Document Type</label>
            <v-select
              :value="data.docTypeIncludedLocal.value"
              :items="data.docTypeSummaryFiltered.value"
              item-text="docType"
              item-value="docType"
              return-object
              multiple
              dense
              solo
              flat
              hide-details
              clearable
              placeholder="All"
              append-icon="mdi-chevron-down"
              :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true }"
              class="filter-select"
              @change="data.docTypeIncludedLocal.value = $event; data.doFilter()"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0 && data.docTypeIncludedLocal.value.length === data.docTypeSummaryFiltered.value.length">All</span>
                <span v-else-if="index === 0">{{ data.docTypeIncludedLocal.value.length }} selected</span>
              </template>
            </v-select>
          </div>

          <!-- Extra user-added column filters -->
          <div v-for="ec in columns.extraColumns.value" :key="'ef' + ec.value" class="filter-group">
            <label class="filter-label">{{ ec.text }}</label>
            <DatePickerTextBox
              v-if="ec.isDate"
              :value="data.extraColumnFilters.value[ec.value]"
              :label="ec.text"
              @input="(v) => data.setExtraColumnFilter(ec.value, v)"
              @change="data.doFilter"
            ></DatePickerTextBox>
            <v-select
              v-else-if="ec.value === 'doc_status_text'"
              :value="data.docStatusIncluded.value"
              :items="data.docStatusSummary.value"
              item-text="status"
              item-value="status"
              return-object
              multiple
              dense
              solo
              flat
              hide-details
              clearable
              placeholder="All"
              append-icon="mdi-chevron-down"
              :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true }"
              class="filter-select"
              @change="data.docStatusIncluded.value = $event; data.doFilter()"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0 && data.docStatusIncluded.value.length === data.docStatusSummary.value.length">All</span>
                <span v-else-if="index === 0">{{ data.docStatusIncluded.value.length }} selected</span>
              </template>
            </v-select>
            <v-autocomplete
              v-else-if="ec.isPersonColumn"
              :value="data.extraColumnFilters.value[ec.value]"
              multiple
              :label="ec.text"
              :placeholder="'Filter by ' + ec.text + '...'"
              :items="data.getDistinctValues(ec.value)"
              dense
              solo
              flat
              hide-details
              clearable
              :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true }"
              class="filter-select"
              @change="data.setFieldFilter('extraColumnFilters', ec.value, $event)"
            ></v-autocomplete>
            <v-text-field
              v-else
              :value="data.extraColumnFilters.value[ec.value]"
              dense
              solo
              flat
              hide-details
              clearable
              :placeholder="'Filter ' + ec.text + '...'"
              class="filter-select"
              @input="data.onExtraTextInput(ec.value, $event)"
              @click:clear="data.setFieldFilter('extraColumnFilters', ec.value, '')"
            ></v-text-field>
          </div>

          <div class="filter-group">
            <label class="filter-label">Created Between</label>
            <DatePickerTextBox
              :value="data.docCreatedDateRange.value"
              label="Select date range"
              @input="(v) => { data.docCreatedDateRange.value = v; }"
              @change="data.doFilter"
            ></DatePickerTextBox>
          </div>

          <div class="filter-actions">
            <v-menu v-model="columns.addFilterMenu.value" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <button class="filter-action-btn" v-on="on">
                  <v-icon small left>mdi-plus</v-icon>
                  Add More Fields
                </button>
              </template>
              <v-card class="column-picker-card">
                <div class="column-picker-header">
                  <button class="column-picker-clear" @click="columns.resetColumnsToDefault">Clear</button>
                </div>
                <v-list dense class="column-picker-list">
                  <v-list-item
                    v-for="field in columns.customiseColumns.value"
                    :key="field.value"
                  >
                    <v-list-item-content>
                      <v-switch
                        class="v-input--reverse v-input--expand"
                        style="width: 200px"
                        dense
                        v-model="columns.dtColumnsEnabled.value"
                        :label="field.text"
                        :value="field.value"
                        @change="columns.addRemoveColumn"
                        hide-details
                      ></v-switch>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-btn icon @click="columns.moveColumn(field, 1)" small v-if="field.allowDown" title="Move column down"><v-icon small>mdi-arrow-down</v-icon></v-btn>
                      <v-btn icon @click="columns.moveColumn(field, -1)" small v-if="field.allowUp" title="Move column up"><v-icon small>mdi-arrow-up</v-icon></v-btn>
                    </v-list-item-icon>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
            <button class="filter-action-link" @click="data.clearAllFilters">Clear All</button>
          </div>
          </template>
        </div>
      </div>

      <!-- Explore Tab -->
      <div v-if="!isSidebarCollapsed && sidebarTab === 'explore'" class="sidebar-content">
        <HierarchySearch
          :items="data.items.value"
          :filters="data.buildFilters()"
          :preSelected="data.hierarchyFilters.value"
          :preSelectedClassifiers="data.classifierFilters.value"
          :hierarchyTypes="data.hierarchyTypes.value"
          :classifierTypes="data.classifierTypes.value"
          @filterChanged="data.onHierarchyFilterChanged"
        ></HierarchySearch>
      </div>
      </div>

      <!-- Toggle Button -->
      <button
        @click="isSidebarCollapsed = !isSidebarCollapsed"
        class="sidebar-toggle-btn"
        :title="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      >
        <v-icon size="16">{{ isSidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
      </button>
    </div>

    <!-- Main Content Panel -->
    <div :class="['main-panel', { expanded: isSidebarCollapsed }]">
      <!-- Header -->
      <div class="levelling-header">
        <!-- Stats Lozenges -->
        <div class="stats-row">
          <div class="stat-lozenge stat-yellow">
            <div class="stat-value">{{ totalRoles.toLocaleString() }}</div>
            <div class="stat-icon"><v-icon>mdi-file-tree-outline</v-icon></div>
            <div class="stat-label">Number of roles</div>
          </div>
          <div class="stat-lozenge stat-green">
            <div class="stat-value">{{ rolesLevelled.toLocaleString() }}</div>
            <div class="stat-icon"><v-icon>mdi-folder-open</v-icon></div>
            <div class="stat-label">Roles Levelled</div>
          </div>
          <div class="stat-lozenge stat-blue">
            <div class="stat-value">{{ rolesToBeLevelled.toLocaleString() }}</div>
            <div class="stat-icon"><v-icon>mdi-text-box-outline</v-icon></div>
            <div class="stat-label">Roles to be Levelled</div>
          </div>
          <div class="stat-lozenge stat-pink">
            <div class="stat-value">{{ rolesInProgress.toLocaleString() }}</div>
            <div class="stat-icon"><v-icon>mdi-briefcase-variant-outline</v-icon></div>
            <div class="stat-label">In Progress</div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="data.isLoading.value" class="loading-state">
        <v-progress-circular indeterminate size="50" color="grey"></v-progress-circular>
      </div>

      <!-- Table -->
      <div v-else class="table-container">
        <v-data-table
          :headers="columns.tableColumns.value"
          :items="data.currentPageItems.value"
          :sort-by="data.sortColumn.value"
          :sort-desc="data.sortDesc.value"
          @update:sort-by="data.sortColumn.value = $event"
          @update:sort-desc="data.sortDesc.value = $event"
          hide-default-footer
          :items-per-page="data.rowsPerPage.value"
          :server-items-length="data.useIndex.value ? data.totalItemCount.value : undefined"
          :loading="data.searchInProgress.value"
          class="levelling-table"
        >
          <template v-slot:item="{ item }">
            <tr @click.stop="emit('openDocument', item)">
              <template v-for="col in columns.tableColumns.value">
                <td v-if="col.value === 'level'" :key="'lvl_' + col.value" @click.stop="emit('editLevel', item)" class="level-cell">
                  <span v-if="item.tpa_job_level" class="d-flex align-center">
                    {{ item.tpa_job_level }}
                    <v-icon small class="ml-1 level-edit-icon" title="Edit Level">mdi-pencil</v-icon>
                  </span>
                  <span v-else-if="evalStatusFor(item) === 'DRAFT'" class="d-flex align-center in-progress">
                    In Progress
                    <v-icon small class="ml-1 level-edit-icon" title="Continue Evaluation">mdi-pencil</v-icon>
                  </span>
                  <a v-else class="set-level-link">Set Level</a>
                </td>
                <td v-else-if="col.value === data.evaluatorColumnField.value" :key="col.value">
                  <span class="small" v-html="sanitize(data.evaluatorValueForItem(item) || data.getEvaluationStatus(item.doc_id).evaluation_created_by)"></span>
                </td>
                <td v-else :key="col.value">
                  <span class="small" v-html="sanitize(item[col.value])"></span>
                </td>
              </template>
            </tr>
          </template>
        </v-data-table>
      </div>

      <!-- Pagination Footer -->
      <div class="pagination-footer">
        <div class="pagination-left">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <button class="action-bar-btn" v-bind="attrs" v-on="on">
                {{ data.rowsPerPage.value }} Rows
                <v-icon small>mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list dense>
              <v-list-item v-for="opt in [10, 20, 30, 50]" :key="opt" @click="handleRowsPerPage(opt)">
                <v-list-item-title>{{ opt }} Rows</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-pagination
            :value="data.currentPage.value"
            :length="data.totalPages.value"
            :total-visible="10"
            @input="(v) => { data.currentPage.value = v; data.onPageChange(); }"
          ></v-pagination>
        </div>
        <div class="pagination-right">
          <PageDescription
            :totalItems="data.totalItemCount.value"
            :pageSize="data.rowsPerPage.value"
            :currentPage="data.currentPage.value"
          />
        </div>
      </div>
    </div>
  </div>
      </div>

      <!-- Job Architecture Tab -->
      <div v-else-if="levellingTabs.activeTab?.key === 'job-architecture'" class="tabItem placeholder-tab">
        <LevellingReports />
      </div>

      <!-- Pay Equity Tab -->
      <div v-else-if="levellingTabs.activeTab?.key === 'pay-equity'" class="tabItem">
        <ReportContainer />
      </div>

      <!-- My Tasks Tab -->
      <div v-else-if="levellingTabs.activeTab?.key === 'my-tasks'" class="tabItem">
        <Tasks />
      </div>

      <!-- Levelling Framework Tab -->
      <div v-else-if="levellingTabs.activeTab?.key === 'levelling-framework'" class="tabItem">
        <LevellingFramework hideTitle />
      </div>
    </div>
  </v-card>
</template>

<style lang="scss" scoped>
.placeholder-tab {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 40px;
  border-radius: 24px;
  max-width: 440px;
  text-align: center;
}

.placeholder-title {
  font-family: "DM Serif Display", serif;
  font-size: 28px;
  font-weight: 400;
  color: #1a1a1a;
  margin: 8px 0 0;
}

.placeholder-subtitle {
  font-size: 15px;
  color: #888888;
  line-height: 1.5;
  margin: 0;
}

.placeholder-badge {
  display: inline-block;
  padding: 6px 18px;
  border-radius: 20px;
  background: #fff3cd;
  color: #856404;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
}

.levellingManager {
  width: 100%;
  height: calc(100vh - 50px);
  max-height: calc(100vh - 50px);
  padding: 0px;
}

.tab-content {
  padding-top: 20px;
}

.tabItem {
  width: 100%;
}

.tabItem.levelling {
  height: calc(100vh - 210px);
  max-height: calc(100vh - 210px);
  overflow: hidden;
  padding: 0;
}

.rm-primary-tab-icon {
  font-size: 50px !important;
}
.rm-primary-tab {
  height: 90px;
  padding: 20px 24px;
  font-family: "DM Serif Display" !important;
  font-weight: 400;
  font-size: 24px;
  line-height: 100%;
  letter-spacing: 0;
  color: #000000;
}
.rm-primary-tab.tab-selected {
  padding: 16px 20px;
}
.rm-box {
  background-color: #FFFFFF;
  border-radius: 24px !important;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, .08) !important;
}

.levelling-container {
  display: flex;
  width: 100%;
  height: 100%;
  padding-top: 40px;
  gap: 16px;
  overflow: hidden;
}

/* ===== SIDEBAR ===== */
.sidebar-wrapper {
  display: flex;
  flex-shrink: 0;
  height: 100%;
  position: relative;
  z-index: 1;
  transition: width 0.3s ease;

  &.collapsed {
    width: 32px;
  }
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 330px;
  min-width: 330px;
  height: 100%;
  background: #ffffff;
  border-width: 1px 1px 1px 0px;
  border-style: solid;
  border-color: #dddddd;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 0px 24px 24px 0px;
  position: relative;
  transition: width 0.3s ease, min-width 0.3s ease, opacity 0.3s ease;
  overflow: hidden;

  &.collapsed {
    width: 0;
    min-width: 0;
    border: none;
    box-shadow: none;
    opacity: 0;
  }
}

.sidebar-toggle-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 40px;
  margin-top: 98px;
  align-self: flex-start;
  flex-shrink: 0;
  background: #eeeeee;
  border: 1px solid #dddddd;
  border-left: none;
  border-radius: 0px 20px 20px 0px;
  cursor: pointer;

  &:hover {
    background: #e0e0e0;
  }

  .v-icon {
    color: #6b7280;
  }

  &:hover .v-icon {
    color: #3b82f6;
  }
}

.sidebar-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.sidebar-tab {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  gap: 4px;
  flex: 1;
  height: 54px;
  border: none;
  border-bottom: 4px solid transparent;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  color: #aaaaaa;
  background: transparent;
  transition: background 0.2s, color 0.2s;

  &.active {
    color: #000000;
    border-bottom-color: #48a401;
  }

  .v-icon {
    color: #aaaaaa !important;
  }
  &.active .v-icon {
    color: #000000 !important;
  }
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* ===== SEARCH FIELD ===== */
.search-field-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 20px;
  background: #ffffff;

  .search-icon {
    color: #999999;
    font-size: 20px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    color: #333333;
    background: transparent;

    &::placeholder {
      color: #aaaaaa;
    }
  }

  .search-clear {
    color: #999999;
    cursor: pointer;
    flex-shrink: 0;
    margin-left: 8px;

    &:hover {
      color: #333333;
    }
  }
}

/* ===== FILTERS CONTAINER ===== */
.filters-container {
  border: 1px solid #dddddd;
  border-radius: 8px;
  overflow: hidden;
}

.filters-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  gap: 8px;
  height: 36px;
  background: #f7f9fb;
  border-bottom: 1px solid #dddddd;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  line-height: 19.6px;
  user-select: none;
}

.filters-label {
  margin-left: 2px;
}

.filter-count {
  font-size: 13px;
  font-weight: 600;
  color: #888888;
  margin-left: 6px;
}

.filter-group {
  padding: 12px 8px;
  border-bottom: none;
  border-top: none;
}

.filter-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 6px;
}

::v-deep .filter-select {
  border: 1px solid #e0e0e0;
  border-radius: 6px;

  .v-input__slot {
    background: #ffffff !important;
    min-height: 38px !important;
    padding: 0 12px !important;
    box-shadow: none !important;
  }

  .v-select__selection {
    font-size: 14px;
    color: #333333;
  }

  input::placeholder {
    color: #aaaaaa;
    font-size: 14px;
  }
}

/* ===== FILTER ACTIONS ===== */
.filter-actions {
  display: flex;
  align-items: center;
  padding: 12px 8px;
  gap: 10px;
}

.filter-action-btn {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f7f9fb;
  font-size: 13px;
  font-weight: 600;
  color: #333333;
  cursor: pointer;

  &:hover {
    background: #eef1f4;
  }

  .v-icon {
    color: #666666;
  }
}

.filter-action-link {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f7f9fb;
  font-size: 13px;
  font-weight: 600;
  color: #333333;
  cursor: pointer;

  &:hover {
    background: #eef1f4;
  }
}

.column-picker-card {
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.column-picker-header {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px 4px;
  border-bottom: 1px solid #e0e0e0;
}

.column-picker-clear {
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  cursor: pointer;
  padding: 4px 12px;
  border: 1px solid #1976d2;
  border-radius: 6px;
  background: transparent;

  &:hover {
    background: #e3f2fd;
  }
}

.column-picker-list {
  overflow-y: auto;
  padding: 8px 12px !important;

  .v-list-item {
    min-height: 40px;
    padding: 0 4px;
  }

  .v-input--switch {
    margin-top: 0;
    padding-top: 0;
  }

  .v-list-item__icon {
    margin: 4px 0;
    min-width: auto;
  }
}

/* ===== MAIN PANEL ===== */
.main-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 24px;
  padding: 25px 28px 0 28px;
  max-height: calc(100vh - 141px);
  overflow-y: auto;
  transition: width 0.3s ease, max-width 0.3s ease;

  &.expanded {
    max-width: calc(100vw - 100px);
  }
}

.levelling-header {
  margin-bottom: 16px;
}

.levelling-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
}

.levelling-description {
  font-size: 14px;
  color: #666666;
  line-height: 1.5;
  margin-bottom: 0;
}

/* ===== STATS LOZENGES ===== */
.stats-row {
  display: flex;
  gap: 16px;
  margin: 16px 0;
}

.stat-lozenge {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 16px 28px;
  border-radius: 24px;
  min-width: 203px;
  height: 110px;
  flex: 1;

  .stat-value {
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
    color: #333333;
  }

  .stat-icon {
    .v-icon {
      font-size: 42px;
    }
  }

  .stat-label {
    width: 100%;
    font-size: 14px;
    font-weight: 600;
    margin-top: auto;
    color: #444444;
  }
}

.stat-yellow {
  background: #fef9e7;
  .stat-icon .v-icon { color: #d4ac0d; }
}

.stat-green {
  background: #f0f5e0;
  .stat-icon .v-icon { color: #8cb820; }
}

.stat-blue {
  background: #e4ecf5;
  .stat-icon .v-icon { color: #4da6c9; }
}

.stat-pink {
  background: #f9e4eb;
  .stat-icon .v-icon { color: #d4568a; }
}

.action-bar-btn {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f7f9fb;
  font-size: 13px;
  font-weight: 600;
  color: #333333;
  cursor: pointer;

  &:hover {
    background: #eef1f4;
  }

  .v-icon {
    color: #666666;
  }
}

/* ===== TABLE ===== */
.table-container {
  flex: 1;
  overflow: auto;
}

::v-deep .levelling-table {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  overflow: visible;

  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: max-content;
  }

  thead th {
    background: #f7f9fb !important;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    color: #555555;
    border-bottom: 2px solid #eeeeee !important;
    white-space: nowrap;
    min-width: 120px;
  }

  tbody tr {
    cursor: pointer;

    &:hover {
      background: #f9fafb !important;
    }

    td {
      border-bottom: 1px solid #f0f0f0 !important;
      padding: 12px 16px;
      font-size: 14px;
    }
  }
}

.actions-cell {
  width: 50px;
  text-align: center;
}

.set-level-link {
  color: #48A401;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
}

.level-edit-icon {
  opacity: 0.5;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
}

.in-progress {
  color: #2B9DBF;
  font-style: italic;
}

.level-cell {
  cursor: pointer;
}

.small {
  font-size: 13px;
}

/* ===== LOADING ===== */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
}

/* ===== PAGINATION ===== */
.pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  flex-shrink: 0;
}
.pagination-left {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}
.pagination-center {
  flex: 1;
  display: flex;
  justify-content: center;
}
.pagination-right {
  flex: 0 0 auto;
}
.pagination-left ::v-deep .v-pagination {
  gap: 4px;
}
.pagination-left ::v-deep .v-pagination .v-pagination__item,
.pagination-left ::v-deep .v-pagination .v-pagination__navigation,
.pagination-left ::v-deep .v-pagination .v-pagination__more {
  min-width: 40px;
  height: 40px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  box-shadow: none;
  background: transparent;
  border: none;
  margin: 0 2px;
}
.pagination-left ::v-deep .v-pagination .v-pagination__item--active {
  background: #4B5563 !important;
  color: #fff !important;
  border-radius: 10px;
}
.pagination-left ::v-deep .v-pagination .v-pagination__navigation {
  color: #555;
}
.pagination-left ::v-deep .v-pagination .v-pagination__navigation .v-icon {
  font-size: 20px;
  color: #333;
}
.pagination-left ::v-deep .v-pagination .v-pagination__item:hover:not(.v-pagination__item--active),
.pagination-left ::v-deep .v-pagination .v-pagination__navigation:hover {
  border: 1px solid #4B5563;
  border-radius: 10px;
}
</style>
